import React, { useState, useMemo, useEffect, useRef } from "react";
import { PartnerFormState, MobileActionState, TicketOption } from "../types";
import {
  getTickets,
  getServiceFeeInfo,
  ServiceFeeInfo,
} from "../services/api/tickets";
import { registerGroupBooking } from "../services/api/partners";
import {
  subscribeToBookingStatus,
  BookingStatusData,
} from "../services/api/realtime";
import {
  CheckCircle2,
  Users,
  Calculator,
  Ticket,
  ChevronRight,
  ArrowLeft,
  Tag,
  AlertCircle,
  TrendingUp,
  ArrowDown,
  Copy,
  Building2,
  User,
  CreditCard,
  Smartphone,
  Download,
  Loader2,
  ShoppingBag,
  ArrowRight
} from "lucide-react";
import Swal from "sweetalert2";

import { PaymentGateway } from "./PaymentGateway";

interface PartnerRegistrationProps {
  setMobileAction?: (action: MobileActionState | null) => void;
  activeCategory?: "personal" | "rombongan" | null;
  onActivate?: () => void;
}

export const PartnerRegistration: React.FC<PartnerRegistrationProps> = ({
  setMobileAction,
  activeCategory,
  onActivate,
}) => {
  const [activeTab, setActiveTab] = useState<"school" | "agent">("school");
  const [step, setStep] = useState<1 | 2>(1);
  const [tickets, setTickets] = useState<TicketOption[]>([]);
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [serviceFeeInfo, setServiceFeeInfo] = useState<ServiceFeeInfo | null>(
    null
  );
  const containerRef = useRef<HTMLDivElement>(null);

  const [form, setForm] = useState<PartnerFormState>({
    type: "school",
    orgName: "",
    picName: "",
    picEmail: "",
    picPhone: "",
    estimatedPax: "30",
    visitDate: "",
    paymentMethod: "loket",
    notes: "",
  });
  const [confirmEmail, setConfirmEmail] = useState("");

  const [bookingStatus, setBookingStatus] = useState<
    "idle" | "processing" | "success"
  >("idle");
  const [bookingCode, setBookingCode] = useState("");
  const [createdBooking, setCreatedBooking] = useState<any | null>(null);
  const [realtimeStatus, setRealtimeStatus] =
    useState<BookingStatusData | null>(null);
  const [isCopied, setIsCopied] = useState(false);
  const voucherCode = "DICKGIZRIWKBS";
  const referralLink = `https://www.gizrabbitland.com?voucher=${voucherCode}`;
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    async function fetchTickets() {
      try {
        const [data, feeInfo] = await Promise.all([
          getTickets(),
          getServiceFeeInfo(),
        ]);
        setTickets(data);
        setServiceFeeInfo(feeInfo);
      } catch (err) {
        console.error("Failed to fetch tickets:", err);
      } finally {
        setIsLoading(false);
      }
    }
    fetchTickets();
  }, []);

  const selectedTicket = useMemo(
    () => tickets.find((t) => t.id === selectedTicketId),
    [selectedTicketId, tickets]
  );

  const simulation = useMemo(() => {
    const pax = parseInt(form.estimatedPax) || 0;
    if (!selectedTicket) return null;

    const basePrice = selectedTicket.price;
    // Gunakan diskon efektif dari service fee info, default 15% jika belum load
    const discountPercent = serviceFeeInfo
      ? serviceFeeInfo.effectiveGroupDiscount / 100
      : 0.15;
    const totalBase = pax * basePrice;
    const discountAmount = totalBase * discountPercent;
    const finalTotal = totalBase - discountAmount;

    return {
      totalBase,
      discountAmount,
      finalTotal,
      pax,
      pricePerPax: finalTotal / pax,
      discountPercent,
      discountPercentDisplay: serviceFeeInfo?.effectiveGroupDiscount ?? 15,
      isInFreePeriod: serviceFeeInfo?.isInFreePeriod ?? true,
      basePrice,
      isValidPax: pax >= 30,
      benefits:
        activeTab === "school"
          ? [
              `Gratis ${Math.floor(pax / 20)} Tiket Guru`,
              "LKS Cetak Gratis",
              "Educational Guide",
            ]
          : ["VIP Gate Priority", "Driver Lunch Voucher", "Marketing Kit"],
    };
  }, [form.estimatedPax, activeTab, selectedTicket, serviceFeeInfo]);

  const isFormValid = useMemo(() => {
    const isPhoneValid = /^628\d{7,17}$/.test(form.picPhone);
    return (
      selectedTicketId &&
      form.orgName &&
      form.picName &&
      form.picEmail &&
      form.picEmail.includes("@") &&
      form.picEmail === confirmEmail &&
      form.picPhone &&
      isPhoneValid &&
      form.estimatedPax &&
      parseInt(form.estimatedPax) >= 30 &&
      form.visitDate
    );
  }, [form, confirmEmail, selectedTicketId]);

  const handleSelectTicket = (id: string) => {
    setSelectedTicketId(id);
    if (onActivate) onActivate();
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!isFormValid || !selectedTicketId) return;

    setBookingStatus("processing");

    try {
      const response = await registerGroupBooking({
        type: activeTab,
        orgName: form.orgName,
        picName: form.picName,
        picEmail: form.picEmail,
        picPhone: form.picPhone,
        estimatedPax: parseInt(form.estimatedPax),
        ticketId: selectedTicketId,
        visitDate: form.visitDate,
        paymentMethod: form.paymentMethod,
        notes: form.notes,
      });

      setBookingCode(response.data.booking_code || response.data.id);
      setCreatedBooking({
        ...response.data,
        status: response.data.payment_status?.toUpperCase() || "PENDING",
      });
      setBookingStatus("success");
    } catch (err) {
      setBookingStatus("idle");
      Swal.fire({
        icon: "error",
        title: "Gagal Mengirim",
        text: "Terjadi kesalahan saat mengirim data. Silakan cek koneksi Anda.",
      });
    }
  };

  const handleReset = () => {
    setBookingStatus("idle");
    setStep(1);
    setSelectedTicketId(null);
    setForm({
      type: activeTab,
      orgName: "",
      picName: "",
      picEmail: "",
      picPhone: "",
      estimatedPax: "30",
      visitDate: "",
      paymentMethod: "loket",
      notes: "",
    });
    setConfirmEmail("");
    setBookingCode("");
    setCreatedBooking(null);
    setRealtimeStatus(null);
  };

  useEffect(() => {
    if (!bookingCode || bookingStatus !== "success") return;

    const unsubscribe = subscribeToBookingStatus(bookingCode, (data) => {
      setRealtimeStatus(data);

      if (data.status === "completed" || data.status === "paid") {
        if (createdBooking) {
          setCreatedBooking({
            ...createdBooking,
            status: data.status.toUpperCase(),
          });
        }

        if (form.paymentMethod === "loket" && data.status === "completed") {
          Swal.fire({
            icon: "success",
            title: "Pembayaran Dikonfirmasi!",
            text: "Tiket rombongan Anda sudah aktif. Terima kasih!",
            showConfirmButton: false,
            timer: 2000,
          });
        }
      }
    });

    return () => {
      unsubscribe();
    };
  }, [bookingCode, bookingStatus, form.paymentMethod]);

  const handleCopyCode = () => {
    if (bookingCode) {
      navigator.clipboard.writeText(bookingCode);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    }
  };

  const handleCopyVoucher = () => {
    navigator.clipboard.writeText(voucherCode);
    Swal.fire({
      icon: "success",
      title: "Kode voucher disalin!",
      text: voucherCode,
      timer: 1500,
      showConfirmButton: false,
    });
  };

  const handleShareReferral = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Voucher Partner GIZ",
          text: `Gunakan kode voucher berikut untuk diskon khusus: ${voucherCode}`,
          url: referralLink,
        });
      } catch (err) {
        // gagal share
      }
    } else {
      navigator.clipboard.writeText(referralLink);
      Swal.fire({
        icon: "success",
        title: "Link referral disalin!",
        text: referralLink,
        timer: 1500,
        showConfirmButton: false,
      });
    }
  };

  const handleBenefitClick = (title: string, desc: string, icon: string) => {
    Swal.fire({
      title: `<span class="font-black uppercase tracking-tight text-xl">${title}</span>`,
      html: `
                <div class="p-4">
                    <div class="text-5xl mb-6 animate-bounce">${icon}</div>
                    <p class="text-sm text-slate-600 font-medium leading-relaxed">${desc}</p>
                </div>
            `,
      confirmButtonText: "OKE, MENGERTI!",
      confirmButtonColor: activeTab === "school" ? "#4F46E5" : "#0F172A",
      customClass: {
        popup: "rounded-[2.5rem]",
        confirmButton:
          "rounded-2xl px-8 py-3 font-black text-[10px] uppercase tracking-widest",
      },
    });
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
        if (!entry.isIntersecting && setMobileAction) {
          setMobileAction(null);
        }
      },
      { threshold: 0.1 }
    );
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [setMobileAction]);

  useEffect(() => {
    if (!isVisible || !setMobileAction) return;

    if (bookingStatus === "success") {
      if (
        form.paymentMethod === "online" &&
        createdBooking?.status === "PENDING"
      ) {
        setMobileAction({
          label: "Selesaikan Pembayaran",
          onClick: () => {},
          disabled: true,
          priority: 1,
        });
      } else {
        setMobileAction({
          label: "Booking Rombongan Baru",
          onClick: handleReset,
          disabled: false,
          priority: 1,
        });
      }
    } else {
      if (step === 1) {
        setMobileAction({
          label:
            selectedTicketId && simulation?.isValidPax
              ? `Lanjut (Rp${simulation.finalTotal.toLocaleString()})`
              : "Pilih Tiket & Min. 30 Pax",
          onClick: () =>
            selectedTicketId && simulation?.isValidPax && setStep(2),
          disabled: !selectedTicketId || !simulation?.isValidPax,
          priority: 1,
        });
      } else {
        setMobileAction({
          label:
            bookingStatus === "processing"
              ? "Memproses..."
              : "Kirim Booking Rombongan",
          onClick: () => handleSubmit(),
          disabled: !isFormValid || bookingStatus === "processing",
          priority: 1,
        });
      }
    }
  }, [
    isVisible,
    isFormValid,
    bookingStatus,
    setMobileAction,
    activeTab,
    form.paymentMethod,
    createdBooking,
    step,
    selectedTicketId,
    form.estimatedPax,
    simulation,
  ]);

  const PriceSimulationCard = () => {
    if (!simulation) return null;
    return (
      <div className="bg-indigo-50 border-2 border-indigo-100 rounded-3xl p-6 animate-slideUp shadow-sm relative overflow-hidden">
        <div className="absolute top-0 right-0 p-4 opacity-10 pointer-events-none">
          <TrendingUp className="w-16 h-16 text-indigo-600" />
        </div>

        <div className="flex justify-between items-center mb-4 border-b border-indigo-100/50 pb-4">
          <div className="flex items-center gap-2">
            <Calculator className="w-4 h-4 text-indigo-600" />
            <span className="text-[10px] font-black text-indigo-900 uppercase tracking-widest">
              Simulasi Penawaran
            </span>
          </div>
          <span className="bg-emerald-500 text-white text-[9px] px-3 py-1 rounded-full font-black animate-pulse shadow-lg shadow-emerald-200 uppercase tracking-widest">
            Grup Diskon {simulation.discountPercentDisplay}%
          </span>
        </div>

        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest">
              Harga Normal ({simulation.pax} Pax)
            </span>
            <span className="text-xs font-bold text-gray-500 line-through">
              Rp {simulation.totalBase.toLocaleString("id-ID")}
            </span>
          </div>

          <div className="flex justify-between items-center bg-white/60 p-3 rounded-2xl border border-white">
            <div className="flex flex-col">
              <span className="text-[8px] font-black text-indigo-400 uppercase leading-none mb-1 tracking-widest">
                Potongan Harga ({simulation.discountPercentDisplay}%)
              </span>
              <span className="text-sm font-black text-emerald-600">
                - Rp {simulation.discountAmount.toLocaleString("id-ID")}
              </span>
            </div>
            <ArrowDown className="w-5 h-5 text-emerald-500" />
          </div>

          <div className="flex justify-between items-end pt-2">
            <div>
              <p className="text-[9px] text-gray-400 font-black uppercase mb-1.5 tracking-widest">
                Estimasi Total Bayar
              </p>
              <p className="font-black text-indigo-900 text-3xl tracking-tighter leading-none">
                Rp {simulation.finalTotal.toLocaleString("id-ID")}
              </p>
            </div>
            <div className="text-right">
              <p className="text-[8px] text-indigo-400 font-black uppercase tracking-widest mb-1">
                Net Per Pax
              </p>
              <p className="text-base font-black text-indigo-600">
                Rp {Math.round(simulation.pricePerPax).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
        {!simulation.isValidPax && (
          <div className="mt-4 flex items-center gap-2 p-3 bg-rose-50 border border-rose-100 rounded-xl text-rose-500">
            <AlertCircle className="w-4 h-4 shrink-0" />
            <p className="text-[9px] font-black uppercase tracking-tight">
              Butuh Min. 30 Peserta untuk Aktifkan Diskon
            </p>
          </div>
        )}
      </div>
    );
  };

  const renderSuccessView = () => {
    const isLoket = form.paymentMethod === "loket";
    const isPending = createdBooking?.status === "PENDING";

    if (!isLoket && isPending) {
      return (
        <div className="h-full flex flex-col items-center justify-center p-6 animate-fadeIn text-center">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
            <PaymentGateway
              booking={createdBooking}
              onSuccess={() => window.location.reload()}
            />
          </div>
        </div>
      );
    }

    if (isLoket && isPending) {
      return (
        <div className="h-full flex flex-col p-6 animate-fadeIn space-y-5">
          <div className="text-center py-3">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <ShoppingBag className="w-8 h-8 text-amber-600" />
            </div>
            <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight">
              Booking Rombongan Berhasil!
            </h3>
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">
              Silakan lakukan pembayaran di loket
            </p>
          </div>

          <div className="bg-slate-950 rounded-[2rem] p-5 text-white relative overflow-hidden shadow-2xl">
            <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/10 rounded-full -mr-16 -mt-16 blur-3xl"></div>
            <div className="relative z-10">
              <div className="flex justify-between items-start mb-5">
                <div>
                  <p className="text-[9px] font-black text-white/40 uppercase tracking-[0.2em] mb-1">
                    Kode Booking
                  </p>
                  <div className="flex items-center gap-3">
                    <h4 className="text-2xl font-black tracking-tighter text-amber-400 font-mono">
                      {bookingCode}
                    </h4>
                    <button
                      onClick={handleCopyCode}
                      className="p-2 bg-white/5 hover:bg-white/10 rounded-xl transition-all"
                    >
                      {isCopied ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      ) : (
                        <Copy className="w-4 h-4 text-slate-400" />
                      )}
                    </button>
                  </div>
                </div>
                <div className="bg-amber-500/20 text-amber-400 px-3 py-1.5 rounded-full text-[9px] font-black uppercase tracking-wider animate-pulse">
                  Menunggu Bayar
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[8px] font-black text-white/40 uppercase tracking-[0.15em] mb-1">
                    Tanggal Kunjungan
                  </p>
                  <p className="text-sm font-bold text-white/90">
                    {new Date(form.visitDate).toLocaleDateString("id-ID", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </p>
                </div>
                <div>
                  <p className="text-[8px] font-black text-white/40 uppercase tracking-[0.15em] mb-1">
                    Jumlah Peserta
                  </p>
                  <p className="text-sm font-bold text-white/90">
                    {form.estimatedPax} pax
                  </p>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-white/10">
                <div className="flex justify-between items-center">
                  <p className="text-[8px] font-black text-white/40 uppercase tracking-[0.15em]">
                    Estimasi Pembayaran
                  </p>
                  <p className="text-xl font-black text-white">
                    Rp {simulation?.finalTotal.toLocaleString("id-ID") || 0}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white border-2 border-slate-100 rounded-[2rem] p-5 text-center shadow-xl">
            <p className="text-[11px] font-black text-slate-900 uppercase tracking-widest mb-4">
              QR Code untuk Loket Rombongan
            </p>
            <div className="bg-white p-3 rounded-2xl border border-slate-100 w-fit mx-auto shadow-inner mb-3">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=GIZPARK-GROUP-${bookingCode}`}
                alt="Ticket QR"
                className="w-36 h-36 mix-blend-multiply"
                loading="lazy"
              />
            </div>
            <p className="text-[9px] text-slate-400 font-bold mb-3">
              Tunjukkan QRCODE ini ke petugas loket rombongan.
            </p>
            <button
              disabled={isDownloading}
              className="w-full py-3 bg-slate-50 text-slate-900 rounded-xl font-black text-[10px] uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-slate-100 transition-all border border-slate-200 disabled:opacity-60 disabled:cursor-not-allowed"
              onClick={async () => {
                setIsDownloading(true);
                try {
                  const response = await fetch(
                    `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=GIZPARK-GROUP-${bookingCode}`
                  );
                  const qrBlob = await response.blob();
                  const qrUrl = window.URL.createObjectURL(qrBlob);

                  const qrImg = new Image();
                  qrImg.crossOrigin = "anonymous";

                  qrImg.onload = () => {
                    const canvas = document.createElement("canvas");
                    const ctx = canvas.getContext("2d");
                    if (!ctx) {
                      setIsDownloading(false);
                      return;
                    }

                    const padding = 40;
                    const qrSize = 300;
                    const infoHeight = 200;
                    canvas.width = qrSize + padding * 2;
                    canvas.height = qrSize + infoHeight + padding * 2;

                    ctx.fillStyle = "#FFFFFF";
                    ctx.fillRect(0, 0, canvas.width, canvas.height);

                    ctx.fillStyle = "#0F172A";
                    ctx.fillRect(0, 0, canvas.width, 50);

                    ctx.fillStyle = "#FFFFFF";
                    ctx.font = "bold 16px Arial";
                    ctx.textAlign = "center";
                    ctx.fillText(
                      "E-TIKET ROMBONGAN - GIZ & RIW",
                      canvas.width / 2,
                      32
                    );

                    ctx.drawImage(qrImg, padding, 70, qrSize, qrSize);

                    const infoY = 70 + qrSize + 20;

                    ctx.fillStyle = "#1E293B";
                    ctx.font = "bold 14px Arial";
                    ctx.textAlign = "center";
                    ctx.fillText(
                      "KODE BOOKING ROMBONGAN",
                      canvas.width / 2,
                      infoY
                    );

                    ctx.fillStyle = "#F59E0B";
                    ctx.font = "bold 24px monospace";
                    ctx.fillText(bookingCode, canvas.width / 2, infoY + 30);

                    ctx.fillStyle = "#64748B";
                    ctx.font = "12px Arial";
                    ctx.fillText(
                      `${form.estimatedPax} Pax • ${
                        selectedTicket?.title || ""
                      }`,
                      canvas.width / 2,
                      infoY + 55
                    );

                    ctx.fillText(
                      `Institusi: ${form.orgName}`,
                      canvas.width / 2,
                      infoY + 75
                    );

                    ctx.fillText(
                      `Kunjungan: ${new Date(form.visitDate).toLocaleDateString(
                        "id-ID",
                        {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        }
                      )}`,
                      canvas.width / 2,
                      infoY + 95
                    );

                    ctx.fillStyle = "#F59E0B";
                    ctx.font = "bold 11px Arial";
                    ctx.fillText(
                      "Tunjukkan tiket ini di loket rombongan.",
                      canvas.width / 2,
                      infoY + 120
                    );

                    canvas.toBlob((blob) => {
                      if (!blob) {
                        setIsDownloading(false);
                        return;
                      }
                      const url = window.URL.createObjectURL(blob);
                      const link = document.createElement("a");
                      link.href = url;
                      link.download = `tiket-rombongan-${bookingCode}.png`;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      window.URL.revokeObjectURL(url);
                      window.URL.revokeObjectURL(qrUrl);
                      setIsDownloading(false);
                    }, "image/png");
                  };

                  qrImg.onerror = () => {
                    setIsDownloading(false);
                    Swal.fire({
                      icon: "error",
                      title: "Gagal",
                      text: "Gagal memuat QR Code. Silakan coba lagi.",
                      confirmButtonColor: "#4F46E5",
                    });
                  };

                  qrImg.src = qrUrl;
                } catch (error) {
                  setIsDownloading(false);
                  Swal.fire({
                    icon: "error",
                    title: "Gagal",
                    text: "Gagal mengunduh E-Ticket. Silakan coba lagi.",
                    confirmButtonColor: "#4F46E5",
                  });
                }
              }}
            >
              {isDownloading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Memproses...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" /> Simpan Ke Galeri
                </>
              )}
            </button>
          </div>

          <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-3">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="space-y-2">
                <p className="text-[11px] font-bold text-amber-800">
                  Petunjuk Pembayaran di Loket Rombongan:
                </p>
                <ol className="text-[10px] text-amber-700 space-y-1 list-decimal ml-4">
                  <li>Download QRCODE atau catat kode booking</li>
                  <li>Tunjukkan ke petugas loket rombongan</li>
                  <li>Lakukan pembayaran sesuai jumlah yang tertera</li>
                  <li>Tiket otomatis aktif setelah dikonfirmasi</li>
                </ol>
              </div>
            </div>
          </div>

          <button
            onClick={handleReset}
            className="w-full py-3.5 bg-slate-900 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-black transition-all shadow-xl hidden md:block"
          >
            Booking Rombongan Baru
          </button>
        </div>
      );
    }

    return (
      <div className="h-full flex flex-col p-6 animate-fadeIn space-y-5">
        <div className="text-center py-3">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
            <CheckCircle2 className="w-8 h-8 text-emerald-600" />
          </div>
          <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight">
            Pembayaran Berhasil!
          </h3>
          <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">
            E-Ticket Rombongan telah aktif dan siap digunakan
          </p>
        </div>

        <div className="bg-slate-950 rounded-[2rem] p-5 text-white relative overflow-hidden shadow-2xl">
          <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/10 rounded-full -mr-16 -mt-16 blur-3xl"></div>
          <div className="relative z-10">
            <div className="flex justify-between items-start mb-5">
              <div>
                <p className="text-[9px] font-black text-white/40 uppercase tracking-[0.2em] mb-1">
                  Kode Booking Rombongan
                </p>
                <div className="flex items-center gap-3">
                  <h4 className="text-2xl font-black tracking-tighter text-indigo-400 font-mono">
                    {bookingCode}
                  </h4>
                  <button
                    onClick={handleCopyCode}
                    className="p-2 bg-white/5 hover:bg-white/10 rounded-xl transition-all"
                  >
                    {isCopied ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    ) : (
                      <Copy className="w-4 h-4 text-slate-400" />
                    )}
                  </button>
                </div>
              </div>
              <div className="bg-emerald-500 text-white text-[9px] font-black px-3 py-1.5 rounded-full uppercase tracking-widest shadow-lg shadow-emerald-500/20">
                {createdBooking?.status === "COMPLETED" ? "Completed" : "Paid"}
              </div>
            </div>

            <div className="space-y-3 border-t border-white/5 pt-4">
              <div className="flex justify-between items-center">
                <span className="text-[10px] font-black text-white/40 uppercase tracking-widest">
                  Item Wahana
                </span>
                <span className="text-xs font-bold text-white uppercase">
                  {selectedTicket?.title}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[10px] font-black text-white/40 uppercase tracking-widest">
                  Tanggal Kunjungan
                </span>
                <span className="text-xs font-bold text-white">
                  {new Date(form.visitDate).toLocaleDateString("id-ID", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[10px] font-black text-white/40 uppercase tracking-widest">
                  Jumlah Peserta
                </span>
                <span className="text-xs font-bold text-white">
                  {form.estimatedPax} Pax
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[10px] font-black text-white/40 uppercase tracking-widest">
                  Total Bayar
                </span>
                <span className="text-sm font-black text-indigo-400">
                  Rp {simulation?.finalTotal.toLocaleString("id-ID") || 0}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border-2 border-slate-100 rounded-[2rem] p-5 text-center shadow-xl">
          <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest mb-3">
            <Users className="w-3 h-3" /> Tiket Rombongan
          </div>
          <p className="text-[11px] font-black text-slate-900 uppercase tracking-widest mb-4">
            QR Code Ticket Masuk Rombongan
          </p>
          <div className="bg-white p-3 rounded-2xl border border-slate-100 w-fit mx-auto shadow-inner mb-4">
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=GIZPARK-GROUP-${bookingCode}`}
              alt="Ticket QR"
              className="w-36 h-36 mix-blend-multiply"
              loading="lazy"
            />
          </div>
          <p className="text-[9px] text-slate-400 font-bold mb-2">
            Tunjukkan QRCODE ini ke petugas.
          </p>
          <button
            disabled={isDownloading}
            className="w-full py-3.5 bg-slate-50 text-slate-900 rounded-2xl font-black text-[10px] uppercase tracking-widest flex items-center justify-center gap-3 hover:bg-slate-100 transition-all border border-slate-200 disabled:opacity-60 disabled:cursor-not-allowed"
            onClick={async () => {
              setIsDownloading(true);
              try {
                const response = await fetch(
                  `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=GIZPARK-GROUP-${bookingCode}`
                );
                const qrBlob = await response.blob();
                const qrUrl = window.URL.createObjectURL(qrBlob);

                const qrImg = new Image();
                qrImg.crossOrigin = "anonymous";

                qrImg.onload = () => {
                  const canvas = document.createElement("canvas");
                  const ctx = canvas.getContext("2d");
                  if (!ctx) {
                    setIsDownloading(false);
                    return;
                  }

                  const padding = 40;
                  const qrSize = 300;
                  const infoHeight = 220;
                  canvas.width = qrSize + padding * 2;
                  canvas.height = qrSize + infoHeight + padding * 2;

                  ctx.fillStyle = "#FFFFFF";
                  ctx.fillRect(0, 0, canvas.width, canvas.height);

                  ctx.fillStyle = "#F59E0B";
                  ctx.fillRect(0, 0, canvas.width, 55);

                  ctx.fillStyle = "#FFFFFF";
                  ctx.font = "bold 14px Arial";
                  ctx.textAlign = "center";
                  ctx.fillText("E-TIKET ROMBONGAN", canvas.width / 2, 25);
                  ctx.font = "bold 12px Arial";
                  ctx.fillText(
                    "GIZ PARK & RIW - KBS SURABAYA",
                    canvas.width / 2,
                    45
                  );

                  ctx.drawImage(qrImg, padding, 75, qrSize, qrSize);

                  const infoY = 75 + qrSize + 25;

                  ctx.fillStyle = "#1E293B";
                  ctx.font = "bold 14px Arial";
                  ctx.textAlign = "center";
                  ctx.fillText(
                    "KODE BOOKING ROMBONGAN",
                    canvas.width / 2,
                    infoY
                  );

                  ctx.fillStyle = "#F59E0B";
                  ctx.font = "bold 28px monospace";
                  ctx.fillText(bookingCode, canvas.width / 2, infoY + 35);

                  ctx.fillStyle = "#64748B";
                  ctx.font = "12px Arial";
                  ctx.fillText(
                    `${form.estimatedPax} Pax - ${selectedTicket?.title || ""}`,
                    canvas.width / 2,
                    infoY + 55
                  );

                  ctx.fillText(
                    `Kunjungan: ${new Date(form.visitDate).toLocaleDateString(
                      "id-ID",
                      {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      }
                    )}`,
                    canvas.width / 2,
                    infoY + 75
                  );

                  ctx.fillStyle = "#F59E0B";
                  ctx.font = "bold 12px Arial";
                  ctx.fillText(
                    "⚠ Tunjukan tiket ini di loket.",
                    canvas.width / 2,
                    infoY + 130
                  );

                  canvas.toBlob((blob) => {
                    if (!blob) {
                      setIsDownloading(false);
                      return;
                    }
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = url;
                    link.download = `tiket-rombongan-${bookingCode}.png`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                    window.URL.revokeObjectURL(qrUrl);
                    setIsDownloading(false);
                  }, "image/png");
                };

                qrImg.onerror = () => {
                  setIsDownloading(false);
                  Swal.fire({
                    icon: "error",
                    title: "Gagal",
                    text: "Gagal memuat QR Code. Silakan coba lagi.",
                    confirmButtonColor: "#F59E0B",
                  });
                };

                qrImg.src = qrUrl;
              } catch (error) {
                setIsDownloading(false);
                Swal.fire({
                  icon: "error",
                  title: "Gagal",
                  text: "Gagal mengunduh E-Ticket. Silakan coba lagi.",
                  confirmButtonColor: "#F59E0B",
                });
              }
            }}
          >
            {isDownloading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Memproses...
              </>
            ) : (
              <>
                <Download className="w-4 h-4" /> Download E-Ticket Rombongan
              </>
            )}
          </button>
        </div>

        <button
          onClick={handleReset}
          className="w-full py-3.5 bg-slate-900 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-black transition-all shadow-xl hidden md:block"
        >
          Booking Rombongan Baru
        </button>
      </div>
    );
  };

  return (
    <section
      id="partner"
      ref={containerRef}
      className={`py-16 md:py-24 bg-gradient-to-br from-indigo-50/50 to-orange-50/50 scroll-mt-20 transition-all duration-500 ${
        activeCategory === "personal"
          ? "opacity-40 grayscale pointer-events-none"
          : "opacity-100"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-giz-primary font-black tracking-[0.4em] uppercase text-[10px] md:text-[12px]">
            Layanan Group & Institusi
          </span>
          <h2 className="text-3xl md:text-5xl font-black text-slate-900 mt-4 tracking-tighter uppercase leading-tight">
            Kemitraan Rombongan
          </h2>
          <p className="text-slate-500 mt-6 max-w-2xl mx-auto text-sm md:text-base font-medium">
            Nikmati fasilitas jalur khusus, edukasi eksklusif, dan{" "}
            <strong className="text-indigo-600">
              diskon rombongan {serviceFeeInfo?.effectiveGroupDiscount ?? 15}%
            </strong>{" "}
            (Min. 30 Pax).
          </p>
        </div>

        <div className="bg-white rounded-[3.5rem] shadow-[0_50px_100px_-20px_rgba(0,0,0,0.15)] overflow-hidden border border-slate-100 flex flex-col lg:flex-row min-h-[750px] relative z-10">
          {/* Sidebar Info */}
          <div
            className={`p-10 lg:p-16 lg:w-5/12 text-white flex flex-col justify-between transition-colors duration-700 ${
              activeTab === "school" ? "bg-giz-primary" : "bg-slate-900"
            }`}
          >
            <div>
              <div className="flex justify-center lg:justify-start mb-12 gap-3 bg-white/10 p-1.5 rounded-[1.5rem] w-fit mx-auto lg:mx-0 border border-white/10 backdrop-blur-md">
                <button
                  onClick={() => {
                    setActiveTab("school");
                    setForm({ ...form, type: "school" });
                  }}
                  className={`px-8 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                    activeTab === "school"
                      ? "bg-white text-giz-primary shadow-xl"
                      : "text-white/60 hover:text-white"
                  }`}
                >
                  Sekolah
                </button>
                <button
                  onClick={() => {
                    setActiveTab("agent");
                    setForm({ ...form, type: "agent" });
                  }}
                  className={`px-8 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                    activeTab === "agent"
                      ? "bg-white text-slate-900 shadow-xl"
                      : "text-white/60 hover:text-white"
                  }`}
                >
                  Agent / Korporasi
                </button>
              </div>

              <div className="animate-fadeIn">
                <h3 className="text-4xl font-black mb-6 uppercase tracking-tighter leading-tight">
                  {activeTab === "school"
                    ? "Study Tour \nEdukasi"
                    : "Group Visit \n& Corporate"}
                </h3>
                <p className="text-white/70 mb-10 leading-relaxed text-sm font-medium">
                  {activeTab === "school"
                    ? "Program belajar interaktif dengan kelinci asli dan satwa digital dalam satu paket khusus siswa."
                    : "Kemitraan strategis untuk travel agent dan perusahaan dengan akses VIP dan penawaran harga khusus."}
                </p>

                <div className="space-y-5">
                  <div
                    onClick={() =>
                      handleBenefitClick(
                        `Diskon Flat ${
                          serviceFeeInfo?.effectiveGroupDiscount ?? 15
                        }%`,
                        `Potongan harga otomatis ${
                          serviceFeeInfo?.effectiveGroupDiscount ?? 15
                        }% untuk seluruh paket wahana dengan minimal pembelian 30 tiket.`,
                        "🏷️"
                      )
                    }
                    className="flex items-center gap-5 p-5 bg-white/5 rounded-[1.8rem] border border-white/10 hover:bg-white/10 transition-all cursor-pointer group"
                  >
                    <div className="bg-white/20 p-3.5 rounded-2xl group-hover:scale-110 transition-transform">
                      <Tag className="w-6 h-6 text-emerald-400" />
                    </div>
                    <div>
                      <p className="font-black text-[11px] uppercase tracking-widest">
                        Diskon Flat{" "}
                        {serviceFeeInfo?.effectiveGroupDiscount ?? 15}%
                      </p>
                      <p className="text-[10px] text-white/50 mt-1 uppercase font-bold">
                        Minimal 30 Peserta
                      </p>
                    </div>
                  </div>
                  <div
                    onClick={() =>
                      handleBenefitClick(
                        "VIP Fast Track Gate",
                        "Lupakan antrean panjang. Rombongan mitra masuk melalui jalur khusus dengan sistem scanning cepat.",
                        "🚀"
                      )
                    }
                    className="flex items-center gap-5 p-5 bg-white/5 rounded-[1.8rem] border border-white/10 hover:bg-white/10 transition-all cursor-pointer group"
                  >
                    <div className="bg-white/20 p-3.5 rounded-2xl group-hover:scale-110 transition-transform">
                      <Users className="w-6 h-6 text-orange-400" />
                    </div>
                    <div>
                      <p className="font-black text-[11px] uppercase tracking-widest">
                        VIP Fast Track Gate
                      </p>
                      <p className="text-[10px] text-white/50 mt-1 uppercase font-bold">
                        Tanpa Antrean Umum
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="hidden lg:block pt-10 border-t border-white/10">
              <p className="text-[9px] font-black text-white/30 uppercase tracking-[0.3em]">
                Partner Growth Hub GIZ Park & RIW Surabaya
              </p>
            </div>
          </div>

          {/* Form Area */}
          <div className="lg:w-7/12 bg-white relative">
            {bookingStatus === "success" ? (
              renderSuccessView()
            ) : (
              <div className="p-8 lg:p-16 h-full flex flex-col">
                <div className="flex items-center gap-5 mb-12">
                  <div
                    className={`w-10 h-10 rounded-2xl flex items-center justify-center font-black text-xs transition-all duration-500 ${
                      step >= 1
                        ? "bg-indigo-600 text-white shadow-xl shadow-indigo-100"
                        : "bg-slate-100 text-slate-400"
                    }`}
                  >
                    1
                  </div>
                  <div
                    className={`h-1.5 flex-1 rounded-full transition-all duration-700 ${
                      step >= 2 ? "bg-indigo-600" : "bg-slate-100"
                    }`}
                  ></div>
                  <div
                    className={`w-10 h-10 rounded-2xl flex items-center justify-center font-black text-xs transition-all duration-500 ${
                      step >= 2
                        ? "bg-indigo-600 text-white shadow-xl shadow-indigo-100"
                        : "bg-slate-100 text-slate-400"
                    }`}
                  >
                    2
                  </div>
                </div>

                {step === 1 ? (
                  <div className="animate-fadeIn space-y-8 flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Ticket className="w-6 h-6 text-indigo-600" />
                      <h3 className="text-2xl font-black text-slate-900 uppercase tracking-tight">
                        Pilih Paket Wahana
                      </h3>
                    </div>
                    <div className="grid gap-1 mb-2">
                      {tickets.map((ticket) => (
                        <div
                          key={ticket.id}
                          onClick={() => handleSelectTicket(ticket.id)}
                          className={`p-4 rounded-[1rem] border-2 cursor-pointer transition-all flex justify-between items-center group active:scale-[0.98] ${
                            selectedTicketId === ticket.id
                              ? "border-indigo-600 bg-indigo-50 shadow-inner"
                              : "border-slate-100 hover:border-slate-200 hover:bg-slate-50"
                          }`}
                        >
                          <div className="flex-1 pr-6 pointer-events-none">
                            <h4
                              className={`font-black text-sm uppercase tracking-tight mb-1 ${
                                selectedTicketId === ticket.id
                                  ? "text-indigo-900"
                                  : "text-slate-700"
                              }`}
                            >
                              {ticket.title}
                            </h4>
                            <p className="text-[10px] text-slate-400 uppercase font-black tracking-widest">
                              Akses Prioritas Rombongan
                            </p>
                          </div>
                          <div className="text-right pointer-events-none">
                            <p
                              className={`font-black text-lg ${
                                selectedTicketId === ticket.id
                                  ? "text-indigo-600"
                                  : "text-slate-900"
                              }`}
                            >
                              Rp {ticket.price.toLocaleString("id-ID")}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="p-6 bg-slate-50 rounded-[2.5rem] border border-slate-100 space-y-2">
                      <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                        Akumulasi Peserta (Min. 30)
                      </label>
                      <div className="flex items-center justify-between bg-white p-2 rounded-[1.8rem] border border-slate-200 shadow-inner">
                        <button
                          type="button"
                          onClick={() =>
                            setForm((prev) => ({
                              ...prev,
                              estimatedPax: String(
                                Math.max(1, Number(prev.estimatedPax) - 1)
                              ),
                            }))
                          }
                          className="w-10 h-10 rounded-2xl bg-slate-100 flex items-center justify-center font-black text-slate-900 hover:bg-slate-200 transition-all active:scale-95"
                        >
                          -
                        </button>
                        <div className="flex flex-col items-center">
                          <input
                            type="number"
                            className="w-20 text-center bg-transparent border-none outline-none font-black text-slate-900 text-3xl p-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            value={form.estimatedPax}
                            onChange={(e) => {
                              const val = parseInt(e.target.value) || 0;
                              setForm((prev) => ({
                                ...prev,
                                estimatedPax: String(Math.max(0, val)),
                              }));
                            }}
                          />
                          <span className="text-[9px] font-black text-indigo-500 uppercase tracking-widest mt-1">
                            Siswa / Peserta
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            setForm((prev) => ({
                              ...prev,
                              estimatedPax: String(
                                Number(prev.estimatedPax) + 1
                              ),
                            }))
                          }
                          className="w-10 h-10 rounded-2xl bg-indigo-600 flex items-center justify-center font-black text-white shadow-xl shadow-indigo-100 hover:bg-indigo-700 transition-all active:scale-95"
                        >
                          +
                        </button>
                      </div>
                    </div>

                    <PriceSimulationCard />

                    <button
                      disabled={!selectedTicketId || !simulation?.isValidPax}
                      onClick={() => setStep(2)}
                      className={`w-full py-6 rounded-[2rem] font-black uppercase tracking-[0.2em] text-xs transition-all flex items-center justify-center gap-3 ${
                        selectedTicketId && simulation?.isValidPax
                          ? "bg-slate-900 text-white shadow-2xl hover:bg-black"
                          : "bg-slate-100 text-slate-400 cursor-not-allowed"
                      }`}
                    >
                      Lanjut Isi Data Rombongan{" "}
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                ) : (
                  <form
                    onSubmit={handleSubmit}
                    className="animate-slideUp space-y-6 flex-1"
                  >
                    <button
                      type="button"
                      onClick={() => setStep(1)}
                      className="text-[10px] font-black text-indigo-500 flex items-center gap-2 hover:text-indigo-700 mb-6 transition-colors bg-indigo-50 px-5 py-2.5 rounded-full w-fit uppercase tracking-widest"
                    >
                      <ArrowLeft className="w-4 h-4" /> Ganti Wahana / Jumlah
                    </button>

                    <div className="grid md:grid-cols-2 gap-5">
                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">
                          Nama Institusi
                        </label>
                        <div className="relative">
                          <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <input
                            required
                            type="text"
                            className="w-full border-2 border-slate-100 rounded-2xl pl-11 pr-5 py-4 bg-slate-50 text-slate-900 focus:bg-white focus:border-indigo-600 outline-none transition-all text-sm font-bold"
                            placeholder="Sekolah / Agent"
                            value={form.orgName}
                            onChange={(e) =>
                              setForm({ ...form, orgName: e.target.value })
                            }
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">
                          Nama PIC Lapangan
                        </label>
                        <div className="relative">
                          <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <input
                            required
                            type="text"
                            className="w-full border-2 border-slate-100 rounded-2xl pl-11 pr-5 py-4 bg-slate-50 text-slate-900 focus:bg-white focus:border-indigo-600 outline-none transition-all text-sm font-bold"
                            placeholder="Nama Lengkap"
                            value={form.picName}
                            onChange={(e) =>
                              setForm({ ...form, picName: e.target.value })
                            }
                          />
                        </div>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-5">
                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">
                          Email Resmi
                        </label>
                        <div className="relative">
                          <CreditCard className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <input
                            required
                            type="email"
                            className="w-full border-2 border-slate-100 rounded-2xl pl-11 pr-5 py-4 bg-slate-50 text-slate-900 focus:bg-white focus:border-indigo-600 outline-none transition-all text-sm font-bold"
                            placeholder="email@gmail.com"
                            value={form.picEmail}
                            onChange={(e) =>
                              setForm({ ...form, picEmail: e.target.value })
                            }
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">
                          Konfirmasi Email
                        </label>
                        <div className="relative">
                          <CreditCard className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <input
                            required
                            type="email"
                            className={`w-full border-2 rounded-2xl pl-11 pr-5 py-4 text-sm font-bold outline-none transition-all ${
                              confirmEmail && form.picEmail !== confirmEmail
                                ? "border-rose-200 bg-rose-50"
                                : "border-slate-100 bg-slate-50 focus:bg-white focus:border-indigo-600"
                            }`}
                            placeholder="Ketik ulang email"
                            value={confirmEmail}
                            onChange={(e) => setConfirmEmail(e.target.value)}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-5">
                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">
                          Nomor WhatsApp
                        </label>
                        <div className="relative">
                          <Smartphone className="absolute left-4 top-[28px] -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <input
                            required
                            type="tel"
                            className="w-full border-2 border-slate-100 rounded-2xl pl-11 pr-5 py-4 bg-slate-50 text-slate-900 focus:bg-white focus:border-indigo-600 outline-none transition-all text-sm font-bold"
                            placeholder="628..."
                            value={form.picPhone}
                            onChange={(e) => {
                              const digitsOnly = e.target.value.replace(
                                /\D/g,
                                ""
                              );
                              if (!digitsOnly) {
                                setForm({ ...form, picPhone: "" });
                                return;
                              }
                              let normalized = digitsOnly;
                              if (normalized.startsWith("0")) {
                                normalized = "62" + normalized.slice(1);
                              }
                              if (!normalized.startsWith("62")) {
                                normalized = "62" + normalized;
                              }
                              if (!normalized.startsWith("628")) {
                                normalized = "628" + normalized.slice(2);
                              }
                              const trimmed = normalized.slice(0, 19);
                              setForm({ ...form, picPhone: trimmed });
                            }}
                          />
                        </div>
                        <small
                          className={`ml-2 text-[10px] ${
                            /^628\d{7,17}$/.test(form.picPhone)
                              ? "text-slate-400"
                              : "text-red-500"
                          }`}
                        >
                          * Nomor WhatsApp harus diawali dengan 628 (contoh:
                          628123456789)
                        </small>
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">
                          Tanggal Kunjungan
                        </label>
                        <input
                          required
                          type="date"
                          min={new Date().toISOString().split("T")[0]}
                          className="w-full border-2 border-slate-100 rounded-2xl px-5 py-4 bg-slate-50 text-slate-900 focus:bg-white focus:border-indigo-600 outline-none transition-all text-sm font-bold"
                          value={form.visitDate}
                          onChange={(e) =>
                            setForm({ ...form, visitDate: e.target.value })
                          }
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">
                        Opsi Pembayaran
                      </label>
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() =>
                            setForm({ ...form, paymentMethod: "loket" })
                          }
                          className={`flex-1 py-4 rounded-2xl border-2 text-[10px] font-black uppercase tracking-widest transition-all ${
                            form.paymentMethod === "loket"
                              ? "border-indigo-600 bg-indigo-50 text-indigo-600 shadow-inner"
                              : "border-slate-100 text-slate-400 bg-slate-50"
                          }`}
                        >
                          LOKET (HARI H)
                        </button>
                        <button
                          type="button"
                          disabled
                          className="flex-1 py-4 rounded-2xl border-2 border-slate-100 text-[10px] font-black uppercase tracking-widest bg-slate-50 text-slate-300 opacity-50 cursor-not-allowed relative"
                        >
                          QRIS / VA (ONLINE)
                          <span className="absolute -top-2 -right-2 bg-amber-500 text-white text-[7px] font-black px-1.5 py-0.5 rounded-full">
                            SOON
                          </span>
                        </button>
                      </div>
                    </div>

                    <PriceSimulationCard />

                    <button
                      type="submit"
                      disabled={bookingStatus === "processing" || !isFormValid}
                      className={`w-full py-6 rounded-[2rem] font-black text-white uppercase tracking-[0.2em] text-xs shadow-2xl transition-all flex items-center justify-center ${
                        activeTab === "school"
                          ? "bg-giz-primary hover:bg-indigo-700 shadow-indigo-100"
                          : "bg-slate-900 hover:bg-black shadow-slate-100"
                      }`}
                    >
                      {bookingStatus === "processing"
                        ? "MEMPROSES DATA..."
                        : "KONFIRMASI BOOKING ROMBONGAN"}
                    </button>
                  </form>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
